import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/latmosfera";

const img = (file: string, alt: string, span: 2 | 3 | 6 = 6) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  wide: span === 6,
  span,
});

const mobile = (file: string, alt: string) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  span: 3 as const,
});

export const latmosfera: CaseStudy = {
  slug: "latmosfera",
  title: "L’atmosfera",
  cardTitle: "Разработка и продвижение сайта дизайн-бюро L’atmosfera",
  subtitle: "Архитектурное и интерьерное бюро",
  industry: { label: "Архитектура и дизайн", slug: "architecture" },
  eyebrow: "Корпоративный сайт · Архитектура и дизайн",
  h1: "Разработка и продвижение сайта дизайн-бюро L’atmosfera",
  lead:
    "Собрали сайт дизайн-бюро L’atmosfera: портфолио проектов, разведение сценариев для частных клиентов и HoReCa, разделы архитектурного и интерьерного проектирования с собственным производством — и ведём продвижение проекта.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить сайт дизайн-бюро" },

  seo: {
    primaryKeyword: "разработка и продвижение сайта дизайн-бюро",
    secondaryKeywords: [
      "сайт для дизайн-студии интерьера",
      "разработка сайта архитектурного бюро",
      "продвижение дизайн-бюро",
      "сайт студии для HoReCa",
    ],
    semanticEntities: [
      "дизайн-бюро",
      "архитектурное проектирование",
      "дизайн интерьера",
      "портфолио проектов",
      "частные клиенты",
      "HoReCa",
      "собственное производство",
    ],
    excludedKeywords: [
      "дизайн интерьера заказать",
      "дизайн ресторана",
      "архитектор Москва",
      "ремонт квартиры",
    ],
    title: "Сайт и продвижение L’atmosfera — кейс HAY.agency",
    description:
      "Кейс HAY.agency: сайт дизайн-бюро L’atmosfera с портфолио, архитектурным и интерьерным проектированием, частными и HoReCa-сценариями.",
    h1: "Разработка и продвижение сайта дизайн-бюро L’atmosfera",
    slug: "latmosfera",
    canonicalUrl: `${SITE_URL}/cases/latmosfera`,
    ogTitle: "Сайт и продвижение дизайн-бюро L’atmosfera",
    ogDescription:
      "Портфолио проектов, архитектура, интерьер, собственное производство и разведение сценариев для частных клиентов и HoReCa.",
    ogImage: `${SITE_URL}${dir}/latmosfera-og.webp`,
    robotsIndex: true,
    datePublished: "2024-01-01",
    dateModified: "2026-09-01",
  },

  links: {
    relatedService: {
      label: "Разработка корпоративных сайтов",
      to: routes.corporate,
      note: "Портфолио, услуги и заявки",
    },
    relatedIndustry: {
      label: "Продвижение экспертных и нишевых бизнесов",
      to: routes.expert,
      note: "SEO и продвижение для B2B-подрядчиков",
    },
    relatedCases: [
      {
        label: "Разработка сайта производителя заборов Artline",
        to: "/cases/artline",
        note: "Каталог продукции и портфолио объектов",
      },
      {
        label: "Комплексное продвижение сети «Городская База Цветов»",
        to: "/cases/gbcvet",
        note: "Сайт и SEO-продвижение сети",
      },
    ],
    relatedArticles: [],
  },

  cta: {
    title: "Нужен сайт дизайн-бюро, который приводит проекты?",
    text: "Спроектируем портфолио, разведём сценарии для частных клиентов и HoReCa, возьмём продвижение на себя.",
    button: "Обсудить сайт дизайн-бюро",
  },

  client: {
    name: "L’atmosfera",
    url: "https://latmosfera.ru/",
    city: "Москва",
    industry: "Архитектура и дизайн интерьера",
  },
  year: "С 2024 года",
  services: [
    "Дизайн сайта дизайн-бюро",
    "Разработка портфолио проектов",
    "Разделы архитектуры, интерьера и производства",
    "SEO-продвижение",
    "Сопровождение проекта",
  ],
  platform: "Tilda",
  tags: [
    { label: "Корпоративный сайт", to: routes.corporate },
    { label: "Разработка", to: routes.sites },
    { label: "SEO", to: routes.seo },
    { label: "Поддержка", to: routes.corporate },
  ],
  facts: [
    { label: "Клиент", value: "L’atmosfera" },
    { label: "Отрасль", value: "Архитектура и дизайн интерьера" },
    { label: "Тип проекта", value: "Сайт дизайн-бюро с портфолио" },
    { label: "Платформа", value: "Tilda" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Сайт бюро с разведением сценариев для частных клиентов и HoReCa",
      "Портфолио крупных проектов с навигацией и детальными страницами",
      "Разделы архитектурного проектирования, дизайна интерьера и производства",
      "Контакты для России и Италии",
      "SEO-продвижение сайта дизайн-бюро",
    ],
  },

  story: [
    {
      title: "Задача",
      paragraphs: [
        "L’atmosfera работает и с частными интерьерами, и с общественными пространствами HoReCa — это разные заказчики с разными вопросами и ожиданиями от портфолио. Сайту нужно было развести эти сценарии, не запутав посетителя с первого экрана.",
        "Вторая часть задачи — показать полный цикл бюро: архитектурное проектирование, дизайн интерьера и собственное производство, — чтобы посетитель понимал реальный масштаб компании.",
      ],
    },
  ],
  features: [],

  design: {
    title: "Дизайн: тёплая архитектурная подача вместо галереи интерьеров",
    paragraphs: [
      "Интерфейс собран на тёплой stone/off-white палитре — фон не спорит с фотографиями интерьеров и держит премиальный тон бюро.",
      "Портфолио выведено крупными карточками с акцентом на фотографии объектов, а не на декоративные элементы интерфейса.",
    ],
    bullets: [
      "Тёплая stone/off-white палитра",
      "Крупные карточки портфолио с доминирующими фото интерьеров",
      "Разведение частных и HoReCa-сценариев с первого экрана",
    ],
  },

  identity: {
    fonts: [
      { name: "Montserrat", role: "Крупные заголовки", sample: "Портфолио" },
      { name: "Inter", role: "Текст и навигация", sample: "Дизайн интерьера" },
    ],
    colors: [
      { hex: "#F2ECE3", name: "Основной фон" },
      { hex: "#8A7A63", name: "Акцент бренда" },
      { hex: "#211E1A", name: "Текст" },
    ],
  },

  cover: img(
    "latmosfera-case-cover",
    "Главная страница сайта дизайн-бюро L’atmosfera с портфолио проектов",
    6,
  ),

  gallery: [
    img("latmosfera-portfolio", "Портфолио проектов дизайн-бюро L’atmosfera", 6),
  ],

  responsive: {
    text: [
      "Мобильную версию проектировали от портфолио: крупные фотографии проектов, минимум интерфейсных элементов и быстрый переход к контактам.",
      "Смысловые блоки — услуги, производство и продвижение — идут на мобильном горизонтальным скроллом.",
    ],
    images: [
      mobile("latmosfera-mobile-home", "Мобильная главная сайта L’atmosfera"),
      mobile("latmosfera-mobile-portfolio", "Мобильное портфолио проектов L’atmosfera"),
    ],
  },

  columnBlocks: [
    {
      id: "audiences",
      title: "Два типа заказчиков",
      paragraphs: [
        "Сайт разводит частных клиентов и HoReCa без длинных пояснений: посетитель сразу видит релевантные проекты и услуги для своего сценария.",
      ],
      columns: [
        { title: "Частные клиенты", items: ["Интерьеры квартир и домов", "Индивидуальные проекты"] },
        { title: "HoReCa", items: ["Общественные пространства", "Проекты для ресторанов и отелей"] },
      ],
    },
    {
      id: "portfolio",
      title: "Портфолио",
      paragraphs: [
        "Крупные проекты вынесены в отдельный раздел с навигацией и детальной страницей каждого объекта — материалы, планировка, визуализации.",
      ],
      bullets: [
        "Навигация по крупным проектам",
        "Детальная страница проекта",
        "Фотографии интерьеров в приоритете",
      ],
    },
    {
      id: "services",
      title: "Услуги и производство",
      paragraphs: [
        "Архитектурное проектирование, дизайн интерьера и собственное производство показаны единой схемой — весь цикл работы бюро на одном экране.",
      ],
      columns: [
        { title: "Архитектура", items: ["Проектирование пространства"] },
        { title: "Интерьер", items: ["Дизайн-проект интерьера"] },
        { title: "Производство", items: ["Собственное производство"] },
      ],
    },
    {
      id: "promotion",
      title: "Продвижение проекта",
      paragraphs: [
        "После запуска сайт остаётся на нашем сопровождении: развиваем поисковую видимость бюро и следим за актуальностью портфолио.",
      ],
      bullets: ["SEO-продвижение сайта дизайн-бюро", "Техническая поддержка и развитие"],
    },
  ],

  servicesDone: {
    title: "Комплекс работ HAY.agency",
    items: [
      { label: "Разработка корпоративного сайта", to: routes.corporate },
      { label: "SEO-продвижение", to: routes.seo },
      { label: "Поддержка и развитие", to: routes.corporate },
    ],
  },
};
